import * as React from "react";
import { useNavigate } from "react-router";

import { useHasOrgRole } from "@/providers/auth/hooks";
import { ExplainedControl } from "@/shared/components/ExplainedControl";
import {
  autoIngestStateOf,
  ONLY_ADMINS_CHANGE_RULES,
  type IngestionRuleRecord,
} from "@/shared/domain/ingestionRules";
import { useUpdateIngestionRule } from "@/shared/domain/ingestionRules/hooks";
import { Popover, RobotoButton, Switch } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import styles from "./AutoIngestSwitch.module.css";

export interface AutoIngestSwitchProps {
  rule: IngestionRuleRecord;
  /** Written beside the switch, such as "Auto" in a card's header. */
  label?: string;
  /** Called with null when a change is sent, then with the error if it fails. */
  onError: (error: unknown) => void;
}

/**
 * Turns a rule's auto-ingest on, or pauses it, as soon as it is flipped. A rule
 * with no action has nothing to turn on, so flipping it opens a popover that
 * sends an admin to the rule editor to choose one. Only org admins can flip it.
 */
export function AutoIngestSwitch({
  rule,
  label,
  onError,
}: AutoIngestSwitchProps) {
  const navigate = useNavigate();
  const isAdmin = useHasOrgRole("admin");
  const updateRule = useUpdateIngestionRule();
  const [needsActionOpen, setNeedsActionOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const state = autoIngestStateOf(rule);
  const disabled = !isAdmin || updateRule.isPending;

  const renderSwitch = (describedBy?: string) => (
    <Switch
      checked={state === "on"}
      disabled={disabled}
      onCheckedChange={(checked) => {
        if (state === "no-action") {
          setNeedsActionOpen(true);
          return;
        }
        onError(null);
        updateRule.mutate(
          { ruleId: rule.rule_id, updates: { auto_ingest: checked } },
          { onError },
        );
      }}
      aria-label={`${rule.name} auto-ingest`}
      aria-describedby={describedBy}
    />
  );

  return (
    <span ref={anchorRef} className={styles.root}>
      {label !== undefined && (
        <span aria-hidden="true" className={styles.label}>
          {label}
        </span>
      )}
      {isAdmin ? (
        renderSwitch()
      ) : (
        <ExplainedControl explanation={ONLY_ADMINS_CHANGE_RULES} disabled>
          {renderSwitch}
        </ExplainedControl>
      )}
      <Popover open={needsActionOpen} onOpenChange={setNeedsActionOpen}>
        <Popover.Content
          anchor={anchorRef}
          align="end"
          className={styles.popup}
        >
          <Popover.Title>Auto-ingest needs an action</Popover.Title>
          <Popover.Description>
            Choose the action and its settings in the rule editor. Auto-ingest
            turns on when you save.
          </Popover.Description>
          <div className={styles.popupActions}>
            <Popover.Close
              render={
                <RobotoButton variant="secondary" size="sm">
                  Cancel
                </RobotoButton>
              }
            />
            <RobotoButton
              size="sm"
              onClick={() =>
                void navigate(Goto.createEditIngestionRuleRoute(rule.rule_id))
              }
            >
              Choose action &rarr;
            </RobotoButton>
          </div>
        </Popover.Content>
      </Popover>
    </span>
  );
}
